import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { api } from "@/lib/api";
import { orgSlugFromPath } from "@/lib/orgDoor";

/** The subset of the org record a door page needs. */
export interface DoorOrg {
  id: number;
  name: string;
  slug: string;
  logo_url: string | null;
}

/**
 * The organization whose door the current auth page is.
 *
 * Returns `{ slug: null, org: null }` on the main site's own doors
 * (/sign-in, /register, /admin/sign-in), and the resolved org on a prefixed
 * door (/ecfa/sign-in, /ecfa/register, /ecfa/admin/sign-in).
 *
 * `slug` is available immediately from the URL — use it for links, so
 * navigation never waits on a request. `org` arrives after the lookup and
 * carries the branding.
 */
export function useOrgDoor(): { slug: string | null; org: DoorOrg | null } {
  const location = useLocation();
  const slug = orgSlugFromPath(location.pathname);
  const [org, setOrg] = useState<DoorOrg | null>(null);

  useEffect(() => {
    if (!slug) {
      setOrg(null);
      return;
    }
    let cancelled = false;
    api
      .get<DoorOrg>(`/orgs/by-slug/${encodeURIComponent(slug)}`)
      .then((data) => {
        if (!cancelled) setOrg(data);
      })
      .catch(() => {
        // Unknown slug: the page still works, just without co-branding. The
        // backend rejects the slug on submit, which is the real guard.
        if (!cancelled) setOrg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { slug, org };
}

/** Prefix a path with the org slug when on an org door. */
export function doorPath(path: string, slug: string | null): string {
  return slug ? `/${slug}${path}` : path;
}
